<template>
  <div class="modal-overlay" :class="{ 'modal-overlay--full': fullscreen }" @click.self="tryClose">
    <div class="modal-card" :class="{ 'modal-card--full': fullscreen }">
      <div class="modal-header">
        <h2 class="modal-title">{{ title }}</h2>
        <button class="btn btn--ghost btn--icon" @click="tryClose">
          <X :size="16" />
        </button>
      </div>

      <slot :requestClose="tryClose" />
    </div>

    <!-- Dialog de confirmação separado, centralizado sobre tudo -->
    <Teleport to="body">
      <div v-if="confirming" class="confirm-backdrop" @click.self="confirming = false">
        <div class="confirm-dialog" role="alertdialog" aria-modal="true">
          <p class="confirm-title">Descartar alterações?</p>
          <p class="confirm-desc">As alterações não salvas serão perdidas.</p>
          <div class="confirm-actions">
            <button class="btn btn--ghost btn--sm" @click="confirming = false">Continuar editando</button>
            <button class="btn btn--danger btn--sm" @click="doClose">Descartar</button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue'
import { X } from 'lucide-vue-next'

export default {
  name: 'AppModal',
  components: { X },
  props: {
    title: { type: String, required: true },
    // Quando false, fechar (X, ESC, clique fora) não pede confirmação --
    // pro chamador que sabe dinamicamente se há algo não salvo em jogo
    // (ex: editor de mapa em tela cheia só confirma com um desenho pendente).
    confirmOnClose: { type: Boolean, default: true },
    // Variante que ocupa a tela inteira (sem max-width/height, sem
    // cantos arredondados) -- usada pelo editor de mapa em tela cheia.
    fullscreen: { type: Boolean, default: false },
    // Off por padrão -- nenhum dos usos existentes de AppModal pediu ESC
    // pra fechar, então isso não muda o comportamento deles. Só o editor de
    // mapa em tela cheia (ticket #146) liga essa opção.
    closeOnEscape: { type: Boolean, default: false },
  },
  emits: ['close'],
  setup(props, { emit }) {
    const confirming = ref(false)

    function tryClose() {
      if (!props.confirmOnClose) {
        emit('close')
        return
      }
      confirming.value = true
    }

    function doClose() {
      confirming.value = false
      emit('close')
    }

    function onKeydown(event) {
      if (props.closeOnEscape && event.key === 'Escape') tryClose()
    }
    onMounted(() => window.addEventListener('keydown', onKeydown))
    onUnmounted(() => window.removeEventListener('keydown', onKeydown))

    return { confirming, tryClose, doClose }
  },
}
</script>

<style scoped>
.confirm-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
}

.confirm-dialog {
  background: #fff;
  border-radius: 14px;
  padding: 24px 28px;
  width: 340px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.confirm-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--ink);
  margin: 0;
}

.confirm-desc {
  font-size: 13px;
  color: #6b7280;
  margin: 0;
}

.confirm-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}
</style>
